export function fmtPace(p: number | null | undefined): string {
  if (!p || p <= 0 || Number.isNaN(p)) return "-";
  const m = Math.floor(p);
  let s = Math.round((p - m) * 60);
  let mm = m;
  if (s === 60) {
    mm += 1;
    s = 0;
  }
  return `${mm}:${s.toString().padStart(2, "0")}/km`;
}

/** Velocidad media en km/h -> "27,4 km/h" (ciclismo). */
export function fmtSpeed(kmh: number | null | undefined, locale = "es-ES"): string {
  if (!kmh || kmh <= 0 || Number.isNaN(kmh)) return "-";
  return `${fmtNum(kmh, 1, locale)} km/h`;
}

/** Ritmo de natación en segundos por 100 m -> "1:38/100m". */
export function fmtPace100(secPer100: number | null | undefined): string {
  if (!secPer100 || secPer100 <= 0 || Number.isNaN(secPer100)) return "-";
  const m = Math.floor(secPer100 / 60);
  let s = Math.round(secPer100 - m * 60);
  let mm = m;
  if (s === 60) {
    mm += 1;
    s = 0;
  }
  return `${mm}:${s.toString().padStart(2, "0")}/100m`;
}

/** Frecuencia cardiaca media -> "152 ppm" / "152 bpm". */
export function fmtHr(h: number | null | undefined, locale = "es-ES"): string {
  if (!h || h <= 0 || Number.isNaN(h)) return "-";
  return `${Math.round(h)} ${locale.startsWith("es") ? "ppm" : "bpm"}`;
}

export function fmtNum(n: number, decimals = 0, locale = "es-ES"): string {
  return n.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function fmtDate(iso: string, locale = "es-ES"): string {
  const d = new Date(iso);
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** Fecha con día de la semana abreviado: "Vie 12 jun 2026" (es) / "Fri 12 Jun 2026" (en). */
export function fmtDateWeekday(iso: string, locale = "es-ES"): string {
  const d = new Date(iso);
  const wdRaw = d.toLocaleDateString(locale, { weekday: "short" }).replace(/\.$/, "");
  const wd = wdRaw.charAt(0).toUpperCase() + wdRaw.slice(1);
  const rest = d.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" });
  return `${wd} ${rest}`;
}

export function fmtDateTime(iso: string, locale = "es-ES"): string {
  const d = new Date(iso);
  return d.toLocaleString(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Fecha numérica corta, según idioma: DD-MM-YYYY (es) / MM-DD-YYYY (en). */
export function fmtDMY(iso: string, locale = "es-ES"): string {
  const d = new Date(iso);
  const p = (x: number) => String(x).padStart(2, "0");
  const dd = p(d.getDate());
  const mm = p(d.getMonth() + 1);
  const yyyy = d.getFullYear();
  return locale.startsWith("en") ? `${mm}-${dd}-${yyyy}` : `${dd}-${mm}-${yyyy}`;
}

/** Duracion en minutos -> "1h 05m" o "42 min". */
export function fmtDur(min: number): string {
  if (!min || min <= 0) return "-";
  const total = Math.round(min);
  if (total < 60) return `${total} min`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}

export function runZone(speed: number | null, zones: { min: number; max?: number }[]): number {
  if (!speed || speed <= 0) return 0;
  for (let i = 0; i < zones.length; i++) {
    const z = zones[i];
    if (z.max === undefined) return i + 1;
    if (speed >= z.min && speed < z.max) return i + 1;
  }
  return zones.length;
}

/** Zona de frecuencia cardiaca (1..n) para una FC media dada. */
export function hrZone(hr: number | null, zones: { min: number; max?: number }[]): number {
  if (!hr || hr <= 0 || !zones.length) return 0;
  for (let i = 0; i < zones.length; i++) {
    const z = zones[i];
    if (z.max === undefined) return i + 1; // última zona, sin techo
    if (hr <= z.max) return i + 1;
  }
  return zones.length;
}

export const CAT_COLORS: Record<string, string> = {
  Carrera: "#FC4C02", // naranja (marca)
  Ciclismo: "#2563eb", // azul
  Natacion: "#06b6d4", // cian
  Hybrid: "#8b5cf6", // violeta (antes Hyrox)
  Hike: "#22c55e", // verde
  Musculacion: "#f59e0b", // ámbar (fuerza/musculación)
  Otros: "#94a3b8", // gris (cajón de sobras)
};

/** Color de una categoría como "r g b" (para la variable CSS --accent con <alpha-value> de Tailwind). */
export function catAccentRGB(cat: string): string {
  const hex = CAT_COLORS[cat] ?? "#FC4C02";
  const n = parseInt(hex.slice(1), 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}
